/* eslint-disable i18next/no-literal-string */
import axios from 'axios';
import { useRef, useState } from 'react';
import { Alert } from 'src/components/Alert';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Card, CardContent, CardHeader } from 'src/components/Card';
import { Chip } from 'src/components/Chip';
import { Divider } from 'src/components/Divider';
import { Meta } from 'src/components/Meta';
import { Stack } from 'src/components/Stack';
import { Tab, Tabs } from 'src/components/Tabs';
import { TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import { AppFrameConfig } from 'src/shared/AppFrame/AppFrameConfig';

type RequestLog = {
  id: number;
  method: string;
  url: string;
  requestBody?: Record<string, unknown>;
  requestHeaders?: Record<string, string>;
  status?: number;
  statusText?: string;
  responseBody?: unknown;
  error?: string;
  timestamp: string;
  durationMs: number;
};

const API_BASE = '/api/mobile/v1';

const rawClient = axios.create({});

function ResponseViewer({ log }: { log: RequestLog }) {
  const isError = !log.status || log.status >= 400;

  return (
    <Card
      variant="outlined"
      sx={{
        bgcolor: isError ? 'error.50' : 'success.50',
        border: 1,
        borderColor: isError ? 'error.200' : 'success.200',
      }}
    >
      <CardContent sx={{ pb: '12px !important' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Chip
            label={log.method}
            size="small"
            color="primary"
            sx={{ fontFamily: 'monospace', fontWeight: 700 }}
          />
          <Typography
            variant="body2"
            sx={{ fontFamily: 'monospace', flex: 1 }}
          >
            {log.url}
          </Typography>
          {log.status && (
            <Chip
              label={`${log.status} ${log.statusText}`}
              size="small"
              color={isError ? 'error' : 'success'}
            />
          )}
          <Typography variant="caption" color="text.secondary">
            {log.durationMs}ms
          </Typography>
        </Stack>

        {log.requestBody && Object.keys(log.requestBody).length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
            >
              Request Body
            </Typography>
            <Box
              component="pre"
              sx={{
                bgcolor: 'grey.900',
                color: 'grey.100',
                p: 1.5,
                borderRadius: 1,
                fontSize: 12,
                overflow: 'auto',
                maxHeight: 200,
                m: 0,
              }}
            >
              {JSON.stringify(log.requestBody, null, 2)}
            </Box>
          </Box>
        )}

        <Typography variant="caption" fontWeight={700} color="text.secondary">
          Response
        </Typography>
        <Box
          component="pre"
          sx={{
            bgcolor: 'grey.900',
            color: isError ? '#ff8a80' : '#b9f6ca',
            p: 1.5,
            borderRadius: 1,
            fontSize: 12,
            overflow: 'auto',
            maxHeight: 400,
            m: 0,
          }}
        >
          {log.error ? log.error : JSON.stringify(log.responseBody, null, 2)}
        </Box>
      </CardContent>
    </Card>
  );
}

function EndpointSection({
  title,
  description,
  method,
  path,
  fields,
  headers,
  onResponse,
}: {
  title: string;
  description: string;
  method: 'GET' | 'POST' | 'DELETE';
  path: string;
  fields: {
    name: string;
    label: string;
    type?: string;
    defaultValue?: string;
  }[];
  headers?: Record<string, string>;
  onResponse?: (data: unknown) => void;
}) {
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(fields.map((f) => [f.name, f.defaultValue || '']))
  );
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<RequestLog[]>([]);
  const logId = useRef(0);

  const handleSend = async () => {
    setLoading(true);
    const start = performance.now();
    const url = `${API_BASE}${path}`;
    const body: Record<string, unknown> = {};
    fields.forEach((f) => {
      if (values[f.name]) body[f.name] = values[f.name];
    });

    const reqHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(headers || {}),
    };

    try {
      const resp = await rawClient.request({
        method,
        url,
        data: method !== 'GET' ? body : undefined,
        params: method === 'GET' ? body : undefined,
        headers: reqHeaders,
        validateStatus: () => true,
      });

      const duration = Math.round(performance.now() - start);
      const log: RequestLog = {
        id: ++logId.current,
        method,
        url,
        requestBody: method !== 'GET' ? body : undefined,
        requestHeaders: reqHeaders,
        status: resp.status,
        statusText: resp.statusText,
        responseBody: resp.data,
        timestamp: new Date().toISOString(),
        durationMs: duration,
      };
      setLogs((prev) => [log, ...prev]);
      onResponse?.(resp.data);
    } catch (err: unknown) {
      const duration = Math.round(performance.now() - start);
      const axiosErr = err as {
        response?: { status: number; statusText: string; data: unknown };
        message?: string;
      };
      const log: RequestLog = {
        id: ++logId.current,
        method,
        url,
        requestBody: method !== 'GET' ? body : undefined,
        status: axiosErr.response?.status,
        statusText: axiosErr.response?.statusText,
        responseBody: axiosErr.response?.data,
        error: axiosErr.response
          ? undefined
          : axiosErr.message || 'Network error',
        timestamp: new Date().toISOString(),
        durationMs: duration,
      };
      setLogs((prev) => [log, ...prev]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card variant="outlined">
      <CardHeader
        title={
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={method}
              size="small"
              color={
                method === 'DELETE'
                  ? 'error'
                  : method === 'GET'
                    ? 'info'
                    : 'primary'
              }
              sx={{ fontFamily: 'monospace', fontWeight: 700 }}
            />
            <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>
              {path}
            </Typography>
          </Stack>
        }
        subheader={description}
      />
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="subtitle2">{title}</Typography>
          {fields.map((field) => (
            <TextField
              key={field.name}
              label={field.label}
              size="small"
              type={field.type || 'text'}
              value={values[field.name]}
              onChange={(e) =>
                setValues((prev) => ({
                  ...prev,
                  [field.name]: (e.target as HTMLInputElement).value,
                }))
              }
              fullWidth
              sx={{ '& input': { fontFamily: 'monospace' } }}
            />
          ))}
          <Button
            variant="prominent"
            onClick={handleSend}
            disabled={loading}
            sx={{ alignSelf: 'flex-start' }}
          >
            {loading ? 'Sending...' : `Send ${method} Request`}
          </Button>

          {logs.length > 0 && (
            <>
              <Divider />
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="subtitle2">
                  Response Log ({logs.length})
                </Typography>
                <Button size="small" onClick={() => setLogs([])}>
                  Clear
                </Button>
              </Stack>
              <Stack spacing={1}>
                {logs.map((log) => (
                  <ResponseViewer key={log.id} log={log} />
                ))}
              </Stack>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

function TokenStore({
  accessToken,
  refreshToken,
  challengeToken,
  onAccessTokenChange,
  onRefreshTokenChange,
  onChallengeTokenChange,
}: {
  accessToken: string;
  refreshToken: string;
  challengeToken: string;
  onAccessTokenChange: (v: string) => void;
  onRefreshTokenChange: (v: string) => void;
  onChallengeTokenChange: (v: string) => void;
}) {
  return (
    <Card
      variant="outlined"
      sx={{ bgcolor: 'primary.50', border: 1, borderColor: 'primary.200' }}
    >
      <CardHeader
        title="Token Store"
        subheader="Tokens are auto-populated on successful login. Edit manually to test specific scenarios."
      />
      <CardContent>
        <Stack spacing={2}>
          <TextField
            label="Access Token"
            size="small"
            fullWidth
            value={accessToken}
            onChange={(e) =>
              onAccessTokenChange((e.target as HTMLInputElement).value)
            }
            sx={{ '& input': { fontFamily: 'monospace', fontSize: 12 } }}
          />
          <TextField
            label="Refresh Token"
            size="small"
            fullWidth
            value={refreshToken}
            onChange={(e) =>
              onRefreshTokenChange((e.target as HTMLInputElement).value)
            }
            sx={{ '& input': { fontFamily: 'monospace', fontSize: 12 } }}
          />
          <TextField
            label="Challenge Token (for 2FA)"
            size="small"
            fullWidth
            value={challengeToken}
            onChange={(e) =>
              onChallengeTokenChange((e.target as HTMLInputElement).value)
            }
            sx={{ '& input': { fontFamily: 'monospace', fontSize: 12 } }}
          />
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="bordered"
              color="error"
              onClick={() => {
                onAccessTokenChange('');
                onRefreshTokenChange('');
                onChallengeTokenChange('');
              }}
            >
              Clear All
            </Button>
            <Button
              size="small"
              variant="bordered"
              onClick={() => onAccessTokenChange('invalid_token_for_testing')}
            >
              Set Invalid Access Token
            </Button>
            <Button
              size="small"
              variant="bordered"
              onClick={() => onRefreshTokenChange('invalid_refresh_token')}
            >
              Set Invalid Refresh Token
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

function WebViewTestPanel({ accessToken }: { accessToken: string }) {
  const [redirectUrl, setRedirectUrl] = useState('/ex4/');
  const [handoffResult, setHandoffResult] = useState<{
    exchangeUrl?: string;
    error?: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const handleRequestHandoff = async () => {
    if (!accessToken) {
      setHandoffResult({ error: 'No access token — login first' });
      return;
    }

    setLoading(true);
    try {
      const resp = await rawClient.post(
        `${API_BASE}/webview_sessions`,
        { redirect_url: redirectUrl },
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          validateStatus: () => true,
        }
      );

      if (resp.status >= 400) {
        setHandoffResult({
          error: `${resp.status}: ${JSON.stringify(resp.data)}`,
        });
      } else {
        setHandoffResult({ exchangeUrl: resp.data.exchange_url });
      }
    } catch (err: unknown) {
      setHandoffResult({
        error: (err as { message?: string }).message || 'Network error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLoadIframe = () => {
    if (handoffResult?.exchangeUrl && iframeRef.current) {
      iframeRef.current.src = handoffResult.exchangeUrl;
    }
  };

  return (
    <Card variant="outlined">
      <CardHeader
        title="WebView Handoff Tester"
        subheader="Simulates the native app → WebView session handoff flow using an iframe"
      />
      <CardContent>
        <Stack spacing={2}>
          <Alert severity="info">
            <strong>Steps:</strong> 1) Enter the redirect URL. 2) Click
            &quot;Request Handoff Token&quot; to get an exchange URL. 3) Click
            &quot;Load in iframe&quot; to simulate WebView navigation. The
            iframe should display the redirected page with a valid Devise
            session.
          </Alert>

          <TextField
            label="Redirect URL"
            size="small"
            fullWidth
            value={redirectUrl}
            onChange={(e) =>
              setRedirectUrl((e.target as HTMLInputElement).value)
            }
            placeholder="/ex4/checkout"
          />

          <Button
            variant="prominent"
            onClick={handleRequestHandoff}
            disabled={loading || !accessToken}
          >
            {loading
              ? 'Requesting...'
              : '1. Request Handoff Token'}
          </Button>

          {handoffResult?.error && (
            <Alert severity="error">{handoffResult.error}</Alert>
          )}

          {handoffResult?.exchangeUrl && (
            <Stack spacing={1}>
              <Typography variant="body2" fontWeight={700}>
                Exchange URL:
              </Typography>
              <Box
                component="pre"
                sx={{
                  bgcolor: 'grey.900',
                  color: '#b9f6ca',
                  p: 1.5,
                  borderRadius: 1,
                  fontSize: 12,
                  overflow: 'auto',
                  m: 0,
                  wordBreak: 'break-all',
                }}
              >
                {handoffResult.exchangeUrl}
              </Box>
              <Button
                variant="prominent"
                onClick={handleLoadIframe}
              >
                2. Load in iframe
              </Button>
            </Stack>
          )}

          <Divider />

          <Typography variant="subtitle2">
            WebView Simulation (iframe)
          </Typography>
          <Box
            sx={{
              border: 2,
              borderColor: 'grey.300',
              borderRadius: 1,
              overflow: 'hidden',
              height: 500,
              bgcolor: 'white',
            }}
          >
            <iframe
              ref={iframeRef}
              title="WebView Test"
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
            />
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function OAuthTestPage() {
  const [activeTab, setActiveTab] = useState(0);
  const [accessToken, setAccessToken] = useState('');
  const [refreshToken, setRefreshToken] = useState('');
  const [challengeToken, setChallengeToken] = useState('');

  const handleLoginResponse = (data: unknown) => {
    const d = data as {
      access_token?: string;
      refresh_token?: string;
      user?: { id: number };
      two_factor_required?: boolean;
      challenge_token?: string;
    };
    if (d.access_token) setAccessToken(d.access_token);
    if (d.refresh_token) setRefreshToken(d.refresh_token);
    if (d.two_factor_required && d.challenge_token)
      setChallengeToken(d.challenge_token);
  };

  const handleRefreshResponse = (data: unknown) => {
    const d = data as {
      access_token?: string;
      refresh_token?: string;
    };
    if (d.access_token) setAccessToken(d.access_token);
    if (d.refresh_token) setRefreshToken(d.refresh_token);
  };

  const authHeaders: Record<string, string> = accessToken
    ? { Authorization: `Bearer ${accessToken}` }
    : {};

  return (
    <>
      <Meta title="OAuth Mobile API Tester" />
      <AppFrameConfig
        id="oauth-test"
        title="OAuth Mobile API Tester"
        containerMaxWidth="lg"
      />

      <Stack spacing={3}>
        <Alert severity="info">
          Test page for the /api/mobile/v1 OAuth endpoints. All requests go to
          the current host ({window.location.host}). Tokens stored in-memory
          only (not persisted).
        </Alert>

        <TokenStore
          accessToken={accessToken}
          refreshToken={refreshToken}
          challengeToken={challengeToken}
          onAccessTokenChange={setAccessToken}
          onRefreshTokenChange={setRefreshToken}
          onChallengeTokenChange={setChallengeToken}
        />

        <Tabs
          value={activeTab}
          onChange={(_e, v) => setActiveTab(v as number)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Login" />
          <Tab label="Auth Lookup" />
          <Tab label="Verify OTP" />
          <Tab label="Resend OTP" />
          <Tab label="Logout" />
          <Tab label="Token Refresh" />
          <Tab label="Token Revoke" />
          <Tab label="WebView Handoff" />
        </Tabs>

        <Box>
          {activeTab === 0 && (
            <EndpointSection
              title="Login"
              description="Authenticate with email/password. Returns tokens or a 2FA challenge. Test errors: wrong password, frozen account, removed user."
              method="POST"
              path="/sessions"
              fields={[
                {
                  name: 'email',
                  label: 'Email',
                  type: 'email',
                },
                {
                  name: 'password',
                  label: 'Password',
                  type: 'password',
                },
                {
                  name: 'org_id',
                  label: 'Org ID (optional, for multi-org users)',
                },
              ]}
              onResponse={handleLoginResponse}
            />
          )}

          {activeTab === 1 && (
            <EndpointSection
              title="Auth Lookup"
              description="Look up which organizations an email belongs to. Used for multi-org login: call this first, let the user pick an org, then pass org_id to login."
              method="POST"
              path="/sessions/auth_lookup"
              fields={[
                {
                  name: 'email',
                  label: 'Email',
                  type: 'email',
                },
              ]}
            />
          )}

          {activeTab === 2 && (
            <EndpointSection
              title="Verify OTP"
              description="Complete 2FA after login returns two_factor_required. The challenge_token is auto-filled from the login response. Test error: send wrong OTP code or expired token."
              method="POST"
              path="/sessions/verify_otp"
              fields={[
                {
                  name: 'challenge_token',
                  label: 'Challenge Token',
                  defaultValue: challengeToken,
                },
                { name: 'otp', label: 'OTP Code' },
              ]}
              onResponse={handleLoginResponse}
            />
          )}

          {activeTab === 3 && (
            <EndpointSection
              title="Resend OTP"
              description="Resend the OTP code via email. Uses the same challenge_token from login."
              method="POST"
              path="/sessions/resend_otp"
              fields={[
                {
                  name: 'challenge_token',
                  label: 'Challenge Token',
                  defaultValue: challengeToken,
                },
              ]}
            />
          )}

          {activeTab === 4 && (
            <EndpointSection
              title="Logout"
              description="Revoke the current access token. Requires a valid Bearer token."
              method="DELETE"
              path="/sessions"
              fields={[]}
              headers={authHeaders}
            />
          )}

          {activeTab === 5 && (
            <EndpointSection
              title="Token Refresh"
              description="Exchange a refresh token for new access + refresh tokens. 30-day rolling expiry (resets on each refresh). Test error: use an invalid or already-used refresh token."
              method="POST"
              path="/tokens/refresh"
              fields={[
                {
                  name: 'refresh_token',
                  label: 'Refresh Token',
                  defaultValue: refreshToken,
                },
              ]}
              onResponse={handleRefreshResponse}
            />
          )}

          {activeTab === 6 && (
            <EndpointSection
              title="Token Revoke"
              description="Revoke the current access token (same as logout)."
              method="POST"
              path="/tokens/revoke"
              fields={[]}
              headers={authHeaders}
            />
          )}

          {activeTab === 7 && (
            <WebViewTestPanel accessToken={accessToken} />
          )}
        </Box>
      </Stack>
    </>
  );
}
